/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { ComponentType, Overlay } from '@angular/cdk/overlay';
import {
  ComponentRef,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  OnDestroy,
  Optional,
  SkipSelf,
  TemplateRef,
  Type,
} from '@angular/core';

import { defer, Observable, Subject } from 'rxjs';
import { Dialog, DialogConfig } from '@angular/cdk/dialog';
import { startWith } from 'rxjs/operators';

import { DialogConfig as SuiDialogConfig } from './dialog-config';
import { DialogContainerComponent } from './dialog-container.component';
import { DialogRef as SuiDialogRef } from './dialog-ref';
import { TypedDialog } from './typed-dialog';

/** Injection token that can be used to access the data that was passed in to a dialog. */
export const SUI_DIALOG_DATA = new InjectionToken<any>('SuiDialogData');

export const SUI_DIALOG_DEFAULT_OPTIONS = new InjectionToken<SuiDialogConfig>(
  'SuiDialogDefaultOptions'
);

// /** Injection token that can be used to specify default dialog options. */
// export const MAT_DIALOG_DEFAULT_OPTIONS = new InjectionToken<SuiDialogConfig>(
//   'mat-mdc-dialog-default-options'
// );

// /** Injection token that determines the scroll handling while the dialog is open. */
// export const MAT_DIALOG_SCROLL_STRATEGY = new InjectionToken<
//   () => ScrollStrategy
// >('mat-mdc-dialog-scroll-strategy', {
//   providedIn: 'root',
//   factory: () => {
//     const overlay = inject(Overlay);
//     return () => overlay.scrollStrategies.block();
//   },
// });

// /**
//  * @docs-private
//  * @deprecated No longer used. To be removed.
//  * @breaking-change 19.0.0
//  */
// export function MAT_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY(
//   overlay: Overlay
// ): () => ScrollStrategy {
//   return () => overlay.scrollStrategies.block();
// }

// /**
//  * @docs-private
//  * @deprecated No longer used. To be removed.
//  * @breaking-change 19.0.0
//  */
// export const MAT_DIALOG_SCROLL_STRATEGY_PROVIDER = {
//   provide: MAT_DIALOG_SCROLL_STRATEGY,
//   deps: [Overlay],
//   useFactory: MAT_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY,
// };

// Counter for unique dialog ids.
let uniqueId = 0;

/**
 * Service to open Material Design modal dialogs.
 */
@Injectable()
export class DialogService implements OnDestroy {
  private readonly _openDialogsAtThisLevel: SuiDialogRef<any>[] = [];
  private readonly _afterAllClosedAtThisLevel = new Subject<void>();
  private readonly _afterOpenedAtThisLevel = new Subject<SuiDialogRef<any>>();
  protected dialogConfigClass = SuiDialogConfig;

  protected readonly _dialogRefConstructor: Type<SuiDialogRef<any>> =
    SuiDialogRef;
  protected readonly _dialogContainerType: Type<any> = DialogContainerComponent;
  protected readonly _dialogDataToken: InjectionToken<any> = SUI_DIALOG_DATA;

  /** Keeps track of the currently-open dialogs. */
  get openDialogs(): SuiDialogRef<any>[] {
    return this._parentDialog
      ? this._parentDialog.openDialogs
      : this._openDialogsAtThisLevel;
  }

  /** Stream that emits when a dialog has been opened. */
  get afterOpened(): Subject<SuiDialogRef<any>> {
    return this._parentDialog
      ? this._parentDialog.afterOpened
      : this._afterOpenedAtThisLevel;
  }

  protected _getAfterAllClosed(): Subject<void> {
    const parent = this._parentDialog;
    return parent
      ? parent._getAfterAllClosed()
      : this._afterAllClosedAtThisLevel;
  }

  /**
   * Stream that emits when all open dialog have finished closing.
   * Will emit on subscribe if there are no open dialogs to begin with.
   */
  readonly afterAllClosed: Observable<void> = defer(() =>
    this.openDialogs.length
      ? this._getAfterAllClosed()
      : this._getAfterAllClosed().pipe(startWith(undefined))
  ) as Observable<any>;

  constructor(
    protected _overlay: Overlay,
    protected _injector: Injector,
    protected _dialog: Dialog,
    @Optional() @SkipSelf() private _parentDialog: DialogService,
    @Optional()
    @Inject(SUI_DIALOG_DEFAULT_OPTIONS)
    private _defaultOptions: SuiDialogConfig
  ) {}

  open<DialogData, DialogResult>(
    component: ComponentType<TypedDialog<DialogData, DialogResult>>,
    config?: SuiDialogConfig<DialogData>
  ): SuiDialogRef<TypedDialog<DialogData, DialogResult>, DialogResult> {
    return this._open(component, config);
  }

  // /**
  //  * Opens a modal dialog containing the given component.
  //  * @param component Type of the component to load into the dialog.
  //  * @param config Extra configuration options.
  //  * @returns Reference to the newly-opened dialog.
  //  */
  // open<T, D = any, R = any>(
  //   component: ComponentType<T>,
  //   config?: SuiDialogConfig<D>
  // ): SuiDialogRef<T, R>;

  // /**
  //  * Opens a modal dialog containing the given template.
  //  * @param template TemplateRef to instantiate as the dialog content.
  //  * @param config Extra configuration options.
  //  * @returns Reference to the newly-opened dialog.
  //  */
  // open<T, D = any, R = any>(
  //   template: TemplateRef<T>,
  //   config?: SuiDialogConfig<D>
  // ): SuiDialogRef<T, R>;

  // open<T, D = any, R = any>(
  //   template: ComponentType<T> | TemplateRef<T>,
  //   config?: SuiDialogConfig<D>
  // ): SuiDialogRef<T, R>;

  _open<T, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: SuiDialogConfig<D>
  ): SuiDialogRef<T, R> {
    let dialogRef: SuiDialogRef<T, R>;
    config = {
      injector: this._injector,
      ...new SuiDialogConfig(),
      ...this._defaultOptions,
      ...config,
    };
    config!.id = `sui-dialog-${uniqueId++}`;

    const backdropClass = ['sui-dialog-backdrop'];
    config.dimmed && backdropClass.push('sui-dialog-backdrop-dimmed');

    const cdkRef = this._dialog.open<R, D, T>(componentOrTemplateRef, {
      ...config,
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy:
        window.innerWidth < (config.responsiveBreakpoint ?? 1200)
          ? this._overlay
              .position()
              .global()
              .bottom('0px')
              .left('0px')
              .right('0px')
          : this._overlay
              .position()
              .global()
              .centerHorizontally()
              .centerVertically(),
      // Disable closing since we need to sync it up to the animation ourselves.
      disableClose: true,
      // Disable closing on destroy, because this service cleans up its open dialogs as well.
      // We want to do the cleanup here, rather than the CDK service, because the CDK destroys
      // the dialogs immediately whereas we want it to wait for the animations to finish.
      closeOnDestroy: false,
      // Disable closing on detachments so that we can sync up the animation.
      // The Material dialog ref handles this manually.
      closeOnOverlayDetachments: false,
      container: {
        type: this._dialogContainerType,
        providers: () => [
          // Provide our config as the CDK config as well since it has the same interface as the
          // CDK one, but it contains the actual values passed in by the user for things like
          // `disableClose` which we disable for the CDK dialog since we handle it ourselves.
          { provide: this.dialogConfigClass, useValue: config },
          { provide: DialogConfig, useValue: config },
        ],
      },
      templateContext: () => ({ dialogRef }),
      providers: (ref, cdkConfig, dialogContainer) => {
        dialogRef = new this._dialogRefConstructor(
          ref,
          config,
          dialogContainer
        );
        return [
          { provide: this._dialogContainerType, useValue: dialogContainer },
          { provide: this._dialogDataToken, useValue: cdkConfig.data },
          { provide: this._dialogRefConstructor, useValue: dialogRef },
        ];
      },
      backdropClass,
    });

    // This can't be assigned in the `providers` callback, because
    // the instance hasn't been assigned to the CDK ref yet.
    (dialogRef! as { componentRef: ComponentRef<T> }).componentRef =
      cdkRef.componentRef!;
    dialogRef!.componentInstance = cdkRef.componentInstance!;

    this.openDialogs.push(dialogRef!);
    this.afterOpened.next(dialogRef!);

    dialogRef!.afterClosed().subscribe(() => {
      const index = this.openDialogs.indexOf(dialogRef);

      if (index > -1) {
        this.openDialogs.splice(index, 1);

        if (!this.openDialogs.length) {
          this._getAfterAllClosed().next();
        }
      }
    });

    return dialogRef!;
  }

  /**
   * Closes all of the currently-open dialogs.
   */
  closeAll(): void {
    this._closeDialogs(this.openDialogs);
  }

  /**
   * Finds an open dialog by its id.
   * @param id ID to use when looking up the dialog.
   */
  getDialogById(id: string): SuiDialogRef<any> | undefined {
    return this.openDialogs.find((dialog) => dialog.id === id);
  }

  ngOnDestroy(): void {
    // Only close the dialogs at this level on destroy
    // since the parent service may still be active.
    this._closeDialogs(this._openDialogsAtThisLevel);
    this._afterAllClosedAtThisLevel.complete();
    this._afterOpenedAtThisLevel.complete();
  }

  private _closeDialogs(dialogs: SuiDialogRef<any>[]): void {
    let i = dialogs.length;

    while (i--) {
      dialogs[i].close();
    }
  }
}
